import { ELCLogger } from 'elc-logging';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface IQueryProvider {
    queryName: string;
    provider: Function;
    context: any;
}
interface IQueryWatcher {
    queryName: string;
    queryWatcher: Function;
}

type QueryCacheType = { [k: string]: boolean | number | string };
export interface IUnsolvedQuery<Q> {
    promise: Promise<any>;
    resolve: Function;
    payload: any;
    queryName: keyof Q;
}

type EventListener = Function;
type NextFnType = Function;
type PayloadType = any;
interface IHook {
    beforeEventListenerRegister?(event: string, listener?: EventListener, next?: NextFnType): void;
    beforeEmit?(action: string, payload?: PayloadType, next?: NextFnType): void;
    afterEmit?(event: string, payload?: PayloadType): void;
    beforeQueryRegister?(queryName: string, qp: IQueryProvider, next: NextFnType): void;
    beforeQuery?(queryName: string, payload?: PayloadType, next?: NextFnType): void;
    afterQuery?(queryName: string, payload?: PayloadType): void;
}
type HooksFunction = <E, Q, QR, C, CR>(bus: ServiceBus<E, Q, QR, C, CR>) => IHook;
type HooksCollection = { [k: string]: Function[] };

export class ServiceBus<E, Q, QR, C, CR> {
    private eventWatchers: Map<any, Function[]>;
    private queryProviders: IQueryProvider[];
    private queryWatchers: IQueryWatcher[];
    private queryCache: QueryCacheType;
    private unsolvedQueries: IUnsolvedQuery<Q>[] = [];
    private hooks: HooksCollection = {};
    private logger: ELCLogger = new ELCLogger('elc-service-bus');

    constructor() {
        this.eventWatchers = new Map();
        this.queryProviders = [];
        this.queryWatchers = [];
        this.queryCache = {};
        // Hooks form a queue, in which the original function is the last in the queue
        this.hooks = {
            beforeEmit: [this.runEmit],
            beforeQuery: [this.runQuery],
            beforeEventListenerRegister: [this.runOn],
            beforeQueryRegister: [this.runRegisterQueryProvider]
        };
        setInterval(this.dirtyCheckQueries, 200);
    }
    public on = <K extends keyof E>(eventName: K, callback: (payload: E[K]) => any) => {
        this.startMark(`on_event_${eventName}`);

        return this.runHooks('beforeEventListenerRegister', eventName, callback);
    };

    private runOn = <K extends keyof E>(eventName: K, callback: (payload: E[K]) => any) => {
        if (!eventName) {
            throw new Error('Event name cannot be undefined while subscribing to ServiceBus');
        }
        if (!callback) {
            throw new Error('A callback is necessary while subscribing to ServiceBus');
        }

        let callbacks = this.eventWatchers.get(eventName);

        if (!callbacks) {
            callbacks = [];
            this.eventWatchers.set(eventName, callbacks);
        }

        if (callbacks) {
            const idx = callbacks.indexOf(callback);
            if (idx < 0) {
                callbacks.push(callback);
            }
        }

        this.endMark(`on_event_${eventName}`);
    };
    public off = (eventName: any, callback?: Function) => {
        if (callback) {
            const callbacks = this.eventWatchers.get(eventName);

            if (callbacks) {
                const idx = callbacks.indexOf(callback);
                if (idx >= 0) {
                    callbacks.splice(idx, 1);

                    if (callbacks.length === 0) {
                        this.eventWatchers.delete(eventName);
                    }
                }
            }
        } else {
            this.eventWatchers.delete(eventName);
        }
    };

    public applyHook = (hook: HooksFunction) => {
        const hookObj = hook(this as ServiceBus<E, Q, QR, C, CR>);
        Object.keys(hookObj).forEach((hookName: keyof IHook) => {
            const hookFn = hookObj[hookName] as any;
            this.hooks[hookName] = this.hooks[hookName] || [];
            const hkCol = this.hooks[hookName];
            if (hookFn && Array.isArray(hkCol)) {
                hkCol.unshift(hookFn);
            }
        });
    };

    public hasUnsolvedQueries = () => {
        return this.unsolvedQueries.length !== 0;
    };

    public emit = <K extends keyof E>(eventName: K, payload?: E[K]) => {
        this.startMark(`emit_${eventName}`);
        this.runHooks('beforeEmit', eventName, payload);
    };

    private runEmit = (eventName: string, payload: any) => {
        const eventWatchers = this.eventWatchers.get(eventName);
        if (eventWatchers) {
            eventWatchers.forEach(c => {
                c(payload);
            });
        }
        this.runHooks('afterEmit', eventName, payload);
        this.endMark(`emit_${eventName}`);
    };

    public query = <K extends keyof Q & keyof QR>(queryName: K, payload?: Q[K]): QR[K] => {
        this.startMark(`query_${queryName}`);

        return this.runHooks('beforeQuery', queryName, payload);
    };
    public command = <K extends keyof C & keyof CR>(commandName: K, payload?: C[K]): CR[K] => {
        this.startMark(`query_${commandName}`);

        return this.runHooks('beforeQuery', commandName, payload);
    };

    private runQuery = <K extends keyof Q>(queryName: K, payload?: Q[K]) => {
        const qp = this.queryProviders.find(p => p.queryName === queryName);
        if (!qp) {
            // eslint-disable-next-line no-new-func
            let resolveFn = new Function();
            const queryPromise = new Promise(resolve => {
                resolveFn = resolve;
            });

            this.unsolvedQueries.push({
                promise: queryPromise,
                resolve: resolveFn,
                payload,
                queryName
            });

            return queryPromise;
        }

        return Promise.resolve(qp.provider.call(qp.context, payload)).then(r =>
            this.afterQuery(qp, r, queryName as string)
        );
    };

    private afterQuery = (qp: IQueryProvider, result: any, queryName: string) => {
        this.runHooks('afterQuery', qp, result);
        this.endMark(`query_${queryName}`);

        return result;
    };

    public registerQueryProvider = <K extends keyof Q & keyof QR>(
        queryName: K,
        provider: (payload: Q[K]) => QR[K],
        context?: Record<string, any>
    ) => {
        return this.runHooks('beforeQueryRegister', queryName, provider, context);
    };
    public registerCommandProvider = <K extends keyof C>(
        commandName: K,
        provider: Function,
        context?: Record<string, any>
    ) => {
        return this.runHooks('beforeQueryRegister', commandName, provider, context);
    };
    private runRegisterQueryProvider = (
        queryName: string,
        provider: Function,
        context?: Record<string, any>
    ) => {
        const existingQueryProviders = this.queryProviders.filter(qp => qp.queryName === queryName);
        if (existingQueryProviders.length) {
            throw new Error(
                `Trying to register a query providerm but there is already a provider for query ${queryName}`
            );
        }
        this.queryProviders.push({ queryName, provider, context });

        const unsolvedProviderQueries = this.unsolvedQueries.filter(
            uq => uq.queryName === queryName
        );
        if (unsolvedProviderQueries.length) {
            unsolvedProviderQueries.forEach(this.executeUnsolvedQuery);
            this.unsolvedQueries = this.unsolvedQueries.filter(uq => uq.queryName !== queryName);
        }
    };

    public registerQueryWatcher = (queryName: any, queryWatcher: Function) => {
        this.queryWatchers.push({ queryName, queryWatcher });
        if (queryName in this.queryCache) {
            queryWatcher.call(null, this.queryCache[queryName]);
        }
    };

    private runHooks = (hookEvent: keyof IHook, ...args: any[]) => {
        const hookChain = this.hooks[hookEvent] || [];

        return this.executeHook(hookChain.slice(0), args);
    };
    private executeHook(hookChain: (Function | undefined)[], args: any) {
        const hookFn = hookChain.shift();
        if (typeof hookFn === 'function') {
            const isLastHook = hookChain.length === 0;
            const next = () => this.executeHook(hookChain.slice(0), args);
            const hookArgs = !isLastHook ? args.concat([next]) : args;

            return hookFn(...hookArgs);
        }
    }
    private executeUnsolvedQuery = ({ queryName, payload, resolve }: IUnsolvedQuery<Q>) => {
        const qp = this.queryProviders.find(p => p.queryName === queryName);
        if (qp) {
            return Promise.resolve(qp.provider.call(qp.context, payload)).then((r: any) => {
                resolve(this.afterQuery(qp, r, queryName as string));

                return r;
            });
        }
    };
    private dirtyCheckQueries = () => {
        const localQueryCache: QueryCacheType = {};
        this.queryWatchers.forEach(qw => {
            const { queryName, queryWatcher } = qw;
            const qp = this.queryProviders.find(p => p.queryName === queryName);
            const queryCache = this.queryCache;
            if (qp) {
                const resultPromise = Promise.resolve(
                    localQueryCache[queryName] || qp.provider.call(qp.context)
                );
                void resultPromise.then(result => {
                    if (result !== queryCache[queryName]) {
                        queryWatcher.call(null, result);
                    }
                    localQueryCache[queryName] = result;
                });
            }
        });
        this.queryCache = localQueryCache;
    };

    private startMark = (name: string) => {
        performance.mark(`service_bus_${name}`);
    };

    private endMark = (name: string) => {
        try {
            performance.mark(`service_bus_${name}_end`);
            performance.measure(
                `service_bus_measure_${name}`,
                `service_bus_${name}`,
                `service_bus_${name}_end`
            );
        } catch (error) {
            this.logger.error({
                message: `[elc-service-bus]: ${error}`,
                triggerType: 'error'
            });
        }
    };
}
